/**
 * Trusted certification document-check results.
 *
 * verifyCredentialDocument (functions/src/verifyCredentialDocument.ts) is the ONLY writer of
 * profiles/{uid}.certificationChecks — firestore.rules forbids any client create/update from touching
 * that field. Results are keyed by the uploaded document's Storage path and record the certification
 * name they were checked for, so a result only ever applies to that exact file under that exact name —
 * pointing another certification entry at an already-checked file does not inherit its status.
 *
 * A client-written certifications[i].aiVerification is untrusted: it is honoured only as the harmless
 * "pending" (checking…) hint. Anything else a candidate writes there — "under_review", "verified" — is
 * ignored everywhere status is read (candidate UI, readiness scoring on client and server,
 * publicProfiles.certificationStatuses).
 *
 * Synced verbatim into functions/src/generated/ (scripts/syncFunctionsGenerated.mjs) so the server
 * resolves status with this exact code.
 */
import type { AiVerification } from '../../types/aiVerification';

export type CertificationChecks = Record<string, AiVerification>;

/** Map key for an uploaded certification document: its Storage object path, sanitized for a Firestore map key. */
export function certificationCheckKey(fileUrl: unknown): string | null {
  if (typeof fileUrl !== 'string' || !fileUrl) return null;
  const match = /\/o\/([^?#]+)/.exec(fileUrl);
  let path: string;
  try {
    path = match ? decodeURIComponent(match[1]) : fileUrl;
  } catch {
    return null;
  }
  const key = path.replace(/[^A-Za-z0-9_-]/g, '_').slice(0, 1400);
  return key || null;
}

/** Certification name as stored on a check result and compared against the entry (trimmed). */
export function certificationCheckName(name: unknown): string {
  return typeof name === 'string' ? name.trim() : '';
}

/**
 * The document-check status to show and score for one certification entry: the server result for
 * exactly this document checked under exactly this certification name when one exists, otherwise at
 * most "pending". Never trusts a client-written status beyond "pending".
 */
export function resolveCertVerification(cert: unknown, checks: unknown): AiVerification | undefined {
  if (!cert || typeof cert !== 'object') return undefined;
  const entry = cert as { name?: unknown; fileUrl?: unknown; aiVerification?: { status?: unknown } | null };
  const key = certificationCheckKey(entry.fileUrl);
  const trusted =
    key && checks && typeof checks === 'object'
      ? (checks as Record<string, AiVerification | undefined>)[key]
      : undefined;
  if (
    trusted &&
    typeof trusted === 'object' &&
    trusted.fileUrl === entry.fileUrl &&
    typeof trusted.certName === 'string' &&
    trusted.certName === certificationCheckName(entry.name)
  ) {
    return trusted;
  }
  if (typeof entry.fileUrl === 'string' && entry.fileUrl && entry.aiVerification?.status === 'pending') {
    return { status: 'pending' };
  }
  return undefined;
}

/**
 * The document-check status for one profileDocuments[] entry — the "Hireable Profile Documents"
 * upload path. Same rules as resolveCertVerification: the trusted server result is the one keyed by
 * this exact document's Storage path, and a client-written status is honoured only as "pending".
 *
 * A credential document with no server result yet is "pending", never "reviewed" — this is what
 * stops an unchecked upload from being counted as a real credential by readiness scoring.
 */
export function resolveDocumentVerification(doc: unknown, checks: unknown): AiVerification | undefined {
  if (!doc || typeof doc !== 'object') return undefined;
  const entry = doc as { downloadUrl?: unknown; status?: unknown };
  if (typeof entry.downloadUrl !== 'string' || !entry.downloadUrl) return undefined;
  const key = certificationCheckKey(entry.downloadUrl);
  const trusted =
    key && checks && typeof checks === 'object'
      ? (checks as Record<string, AiVerification | undefined>)[key]
      : undefined;
  if (trusted && typeof trusted === 'object' && trusted.fileUrl === entry.downloadUrl) {
    return trusted;
  }
  return entry.status === 'pending' ? { status: 'pending' } : undefined;
}
